import * as React from "react";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { useGlobalAuthContext } from "../context/AuthContext";
import { DynamicAlert } from "../utils/Alerts";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { login } from "../Slices/roomNateSlice";
import { ReactComponent as LogoIcon } from "../Assets/mf-logo.svg";
import { ReactComponent as LeftArrowIcon } from "../Assets/LeftArrow.svg";
import Cookies from "js-cookie";
import { baseUrl } from "../utils/baseUrl";
import { getSafeUserPostAuthDestination } from "../utils/postAuthRedirect";
import {
  getGuestLayoutUuids,
  clearGuestLayouts,
} from "../utils/guestLayoutStorage";
import { getGuestSessionId } from "../utils/guestSession";
import {
  DUPLICATE_EMAIL_MESSAGE,
  RATE_LIMITED_MESSAGE,
  isDuplicateEmailError,
  isRateLimitedError,
} from "../utils/signupErrors";
import AuthLegalStrip from "../Components/Auth/AuthLegalStrip";
import AuthSwitchLink from "../Components/Auth/AuthSwitchLink";
import AuthPasswordField from "../Components/Auth/AuthPasswordField";
import AuthPageLayout from "../Components/Auth/AuthPageLayout";
import {
  getAuthGlassFieldSx,
  getAuthPrimaryButtonSx,
} from "../Components/Auth/authGlassStyles";

export default function SignUp() {
  const theme = useTheme();
  const { dispatch } = useGlobalAuthContext();
  const Dispatch = useDispatch();
  const [formKey, setFormKey] = useState(0);


  const { currentUserSessionStorage , autoApproveUser } = useGlobalAuthContext();

  // =====================================================
  // DIAGNOSTIC: Log sessionStorage on component mount
  // =====================================================
  React.useEffect(() => {
    console.log('');
    console.log('╔' + '═'.repeat(60) + '╗');
    console.log('║  📍 [SIGNUP PAGE] Component Mounted                        ║');
    console.log('╠' + '═'.repeat(60) + '╣');
    console.log('║  Current sessionStorage values:');
    console.log('║    redirectPath:', sessionStorage.getItem("redirectPath") || '(not set)');
    console.log('║    needsCopy:', sessionStorage.getItem("needsCopy") || '(not set)');
    console.log('║    layoutIdToCopy:', sessionStorage.getItem("layoutIdToCopy") || '(not set)');
    console.log('║  ');
    console.log('║  currentUserSessionStorage:', !!currentUserSessionStorage);
    console.log('╚' + '═'.repeat(60) + '╝');
    console.log('');
  }, [currentUserSessionStorage]);

  const [load, setLoad] = React.useState(false);
  const navigate = useNavigate();
  const [showPsw, setShowPsw] = React.useState(false);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState(false);

  const initialValuesUser = {
    name: "",
    email: "",
    password: "",
    role: "user"
  };

  const formik = useFormik({
    key: formKey,
    initialValues: initialValuesUser,
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required("Enter your name")
        .max(80, "Name is too long"),
      email: Yup.string()
        .required("Enter your email")
        .matches(
          "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
          "Enter a valid email address"
        ),
      password: Yup.string()
        .required("Choose a password")
        .min(8, "Password must be at least 8 characters."),
    }),
    onSubmit: async (values, { resetForm }) => {
      let data;
      const { email, role, password, name } = values;
      const trimmedName = name?.trim();
      data = { email, role, password };
      if (trimmedName) data.name = trimmedName;
      const guestUuids = getGuestLayoutUuids();
      if (guestUuids.length > 0) {
        data.guestLayoutUuids = guestUuids;
      }
      // Claim any layouts uploaded under this browser's guest session — covers
      // uploads whose completion the client never saw (user left before
      // processing finished), which the uuid list alone misses. Send both
      // casings: the auth-time session claim reads snake_case guest_session_id,
      // while the server-emitted account_created event reads camelCase
      // guestSessionId — sending both keeps session-join working either way.
      const guestSessionId = getGuestSessionId();
      if (guestSessionId) {
        data.guest_session_id = guestSessionId;
        data.guestSessionId = guestSessionId;
      }
      // Standalone /signup page → "direct" origin. The backend stamps this on
      // its authoritative account_created event so /signups-breakdown can split
      // editor vs direct without relying on a client-emitted duplicate.
      data.signup_source = "direct";
      setLoad(true);
      try {
        const response = await axios.post("/user/signup/", data);
        setLoad(false);
        // account_created is emitted server-side (createAccountCreatedEvent on
        // /user/signup) as the single source of truth. It carries the identity,
        // guestSessionId and signup_source we send above, so a client-side emit
        // here would double-count signups/conversion. Intentionally not logged.

        if (window.opener) {
        try {
          const openerPath = new URL(window.opener.location.href).pathname;
          if (openerPath === "/main") {
            window.opener.postMessage(
              {
                authToken: response?.data?.token,
                role: response?.data?.user?.role,
                status: "success",
              },
              window.location.origin
            );
            window.close();
          }
        } catch (err) {
          console.warn("Access denied to opener location:", err);
        }
      }
        
        if (response?.data?.user?.role === "user" && response?.data?.user?.status === "blocked") {
          setStatus(true);
          setTitle("We’ll email you as soon as your account is approved.");
          resetForm({ values: { ...initialValuesUser } });
          setFormKey((key) => key + 1);
        } else{
          dispatch({ type: "LOGIN", payload: response?.data?.token });
          Cookies.set("token", JSON.stringify(response?.data?.token), {
          expires: 7,
          });
          Dispatch(login(response?.data?.token));
          if (guestUuids.length > 0) {
            clearGuestLayouts(guestUuids);
          }
        }

        // =====================================================
        // PR #93: SIGNUP SUCCESS - COPY FIRST, THEN NAVIGATE ONCE
        // =====================================================
        // Same pattern as login: copy FIRST (blocking), then navigate ONCE
        // User must NEVER enter original UUID editor (overwrite risk)
        // =====================================================
        const redirectPath = sessionStorage.getItem("redirectPath");
        const needsCopy = sessionStorage.getItem("needsCopy") === "true";
        const layoutIdToCopy = sessionStorage.getItem("layoutIdToCopy");

        const role = response?.data?.user?.role;
        const status = response?.data?.user?.status;
        const authToken = response?.data?.token;
        
        console.log('');
        console.log('╔' + '═'.repeat(70) + '╗');
        console.log('║  📝 [SIGNUP SUCCESS] Handler executing                              ║');
        console.log('╠' + '═'.repeat(70) + '╣');
        console.log('║  RESPONSE DATA:');
        console.log('║    role:', role);
        console.log('║    status:', status);
        console.log('║    userId:', response?.data?.user?._id);
        console.log('║    token exists:', !!authToken);
        console.log('║  ');
        console.log('║  SESSION STORAGE VALUES (BEFORE clearing):');
        console.log('║    redirectPath:', redirectPath || '(not set)');
        console.log('║    needsCopy (raw):', sessionStorage.getItem("needsCopy"));
        console.log('║    needsCopy (parsed):', needsCopy);
        console.log('║    layoutIdToCopy:', layoutIdToCopy || '(not set)');
        console.log('║  ');
        console.log('║  DECISION LOGIC:');
        console.log('║    Is user blocked?', status === "blocked");
        console.log('║    Will skip copy (needsCopy=false)?', !needsCopy);
        console.log('║    Will attempt copy (needsCopy=true)?', needsCopy);
        console.log('║    Has layoutIdToCopy?', !!layoutIdToCopy);
        console.log('╚' + '═'.repeat(70) + '╝');
        console.log('');

        // Clear sessionStorage flags immediately (prevents loops/races)
        sessionStorage.removeItem("redirectPath");
        sessionStorage.removeItem("needsCopy");
        sessionStorage.removeItem("layoutIdToCopy");
        console.log('[SIGNUP] Cleared sessionStorage flags');

        if (role === "user") {
          if (status === "blocked") {
            console.log('[SIGNUP] User is blocked, navigating to /login');
            navigate("/login");
            return;
          }

          // =====================================================
          // CASE 1: needsCopy=false - navigate directly (once)
          // =====================================================
          // FIX: Use window.location.href instead of navigate()
          // 
          // ROOT CAUSE: navigate() is async. After auth state changes (dispatch, 
          // Cookies.set), React re-renders BEFORE navigate completes. This causes
          // the route to reset to "/" which then redirects to "/main".
          //
          // SOLUTION: window.location.href performs a hard redirect that bypasses
          // React's reconciliation entirely, guaranteeing user lands on the correct
          // destination.
          // =====================================================
          if (!needsCopy) {
            const destination = getSafeUserPostAuthDestination(redirectPath);
            console.log('');
            console.log('╔' + '═'.repeat(70) + '╗');
            console.log('║  🚀 [SIGNUP] CASE 1: needsCopy=false - DIRECT NAVIGATION          ║');
            console.log('╠' + '═'.repeat(70) + '╣');
            console.log('║  redirectPath:', redirectPath || '(not set)');
            console.log('║  Final destination:', destination);
            console.log('║  ');
            console.log('║  ⚡ Using window.location.href (hard redirect)');
            console.log('║  (bypasses React re-render race condition)');
            console.log('╚' + '═'.repeat(70) + '╝');
            console.log('');
            window.location.href = destination;
            return;
          }
          
          // =====================================================
          // CASE 2: needsCopy=true - COPY FIRST, then navigate (once)
          // =====================================================
          console.log('');
          console.log('╔' + '═'.repeat(70) + '╗');
          console.log('║  🔄 [SIGNUP] CASE 2: needsCopy=true - COPY FIRST                  ║');
          console.log('╠' + '═'.repeat(70) + '╣');
          console.log('║  layoutIdToCopy:', layoutIdToCopy || '(not set)');
          console.log('║  redirectPath:', redirectPath || '(not set)');
          console.log('╚' + '═'.repeat(70) + '╝');
          console.log('');
          
          if (layoutIdToCopy) {
            try {
              const pathCopy = `/savedLayout/copy/${layoutIdToCopy}`;
              console.log('[SIGNUP] ⏳ Copying layout...');
              console.log('[SIGNUP]   API endpoint:', `${baseUrl}${pathCopy}`);
              
              const copyResponse = await axios.post(
                `${baseUrl}${pathCopy}`,
                { parent: null },
                { headers: { Authorization: `Bearer ${authToken}` } }
              );
              const newUuid = copyResponse?.data?.data?.uuid;
              console.log('[SIGNUP] ✅ Layout copied successfully!');
              console.log('[SIGNUP]   New UUID:', newUuid);
              console.log('[SIGNUP]   Full response data:', JSON.stringify(copyResponse?.data?.data, null, 2));

              // Navigate to the NEW UUID (never the original)
              if (redirectPath?.includes("/floor-plan/") && newUuid) {
                const versionMatch = redirectPath.match(/version\/([^/]+)/);
                const version = versionMatch ? versionMatch[1] : "1";
                const newRedirectPath = `/floor-plan/${newUuid}/version/${version}`;
                console.log('');
                console.log('╔' + '═'.repeat(70) + '╗');
                console.log('║  🚀 [SIGNUP] NAVIGATING TO COPIED LAYOUT                         ║');
                console.log('╠' + '═'.repeat(70) + '╣');
                console.log('║  Original redirectPath:', redirectPath);
                console.log('║  Extracted version:', version);
                console.log('║  New UUID:', newUuid);
                console.log('║  Final destination:', newRedirectPath);
                console.log('║  ');
                console.log('║  ⚡ Using window.location.href (hard redirect)');
                console.log('╚' + '═'.repeat(70) + '╝');
                console.log('');
                window.location.href = newRedirectPath;
                return;
              } else {
                console.log('[SIGNUP] ⚠️ Condition failed:');
                console.log('[SIGNUP]   redirectPath includes /floor-plan/:', redirectPath?.includes("/floor-plan/"));
                console.log('[SIGNUP]   newUuid exists:', !!newUuid);
                console.log('[SIGNUP]   Falling through to /main...');
              }
            } catch (error) {
              console.error("[SIGNUP] ❌ Error copying layout:", error);
              console.error("[SIGNUP]   Error message:", error?.message);
              console.error("[SIGNUP]   Error response:", error?.response?.data);
              // Copy failed - go to dashboard, don't risk overwriting original
              console.log('[SIGNUP] Copy failed, using window.location.href to /main for safety');
              window.location.href = "/main";
              return;
            }
          } else {
            console.log('[SIGNUP] ⚠️ No layoutIdToCopy provided!');
          }
          
          // Fallback: no layoutIdToCopy or no redirectPath
          console.log('');
          console.log('╔' + '═'.repeat(70) + '╗');
          console.log('║  ⚠️ [SIGNUP] FALLBACK - No valid copy/redirect                    ║');
          console.log('╠' + '═'.repeat(70) + '╣');
          console.log('║  layoutIdToCopy:', layoutIdToCopy || '(not set)');
          console.log('║  redirectPath:', redirectPath || '(not set)');
          console.log('║  ');
          console.log('║  ⚡ Using window.location.href = "/main"');
          console.log('╚' + '═'.repeat(70) + '╝');
          console.log('');
          window.location.href = "/main";
        } else {
          console.log('[SIGNUP] Non-user role, using window.location.href to /login');
          window.location.href = "/login";
        }
              
      } catch (error) {
        setLoad(false);
        // A taken email isn't a form error — send them to sign-in with the
        // address they just typed instead of surfacing a raw backend string.
        if (isDuplicateEmailError(error)) {
          navigate("/login", {
            state: { email, notice: DUPLICATE_EMAIL_MESSAGE },
          });
          return;
        }
        // Left on screen rather than auto-dismissed after 2s like the errors
        // below: retrying immediately cannot succeed, so the user needs time
        // to actually read it.
        if (isRateLimitedError(error)) {
          setTitle(RATE_LIMITED_MESSAGE);
          setStatus(false);
          return;
        }
        // Backend errors are usually plain-text. A JSON body (the limiter's, or
        // any future structured error) has no `.length`, so the alert's
        // `title?.length >= 1` guard would silently render nothing — coerce
        // anything non-string to copy the user can actually see.
        const responseBody = error?.response?.data;
        setTitle(
          typeof responseBody === "string" && responseBody.trim()
            ? responseBody
            : "Something went wrong. Please try again."
        );
        setStatus(false);
        setTimeout(() => {
          setTitle("");
        }, 2000);
      }
    },
  });

  // Track if user was already logged in when component mounted
  const wasLoggedInOnMount = React.useRef(!!currentUserSessionStorage);
  
  React.useEffect(() => {
    // Only redirect if user was ALREADY logged in when page loaded
    // Don't redirect if they just signed up (the submit handler handles that)
    if (currentUserSessionStorage && wasLoggedInOnMount.current) {
      navigate("/");
    }
  }, [currentUserSessionStorage, navigate]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      {title?.length >= 1 && <DynamicAlert status={status} title={title} />}

      <AuthPageLayout
        footer={
          <>
            <Box sx={{ mt: 2.5, textAlign: "center" }}>
              <AuthSwitchLink
                mutedText="Already have an account?"
                linkText="Log in"
                to="/login"
                sx={{ mt: 0 }}
              />
            </Box>
            <AuthLegalStrip sx={{ pt: 1.5, pb: 0.5 }} />
          </>
        }
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
          <LogoIcon width={52} height={52} />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "21px", sm: "22px" },
            fontWeight: 600,
            color: "text.primary",
            letterSpacing: "-0.02em",
            mb: 2.5,
            textAlign: "center",
            lineHeight: 1.25,
            px: { xs: 0.5, sm: 1 },
          }}
        >
          Join MagicFurnish
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={1.75}>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : "Full name"
                }
                required
                fullWidth
                id="name"
                name="name"
                InputProps={{ style: { height: 48 } }}
                inputProps={{
                  autoComplete: "name",
                  autoCapitalize: "words",
                  autoCorrect: "off",
                  spellCheck: false,
                  enterKeyHint: "next",
                }}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                className={
                  formik.touched.name && formik.errors.name ? null : "white"
                }
                sx={getAuthGlassFieldSx(theme)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : "Email"
                }
                required
                fullWidth
                id="email"
                name="email"
                type="email"
                InputProps={{ style: { height: 48 } }}
                inputProps={{
                  autoComplete: "email",
                  inputMode: "email",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  enterKeyHint: "next",
                }}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                className={
                  formik.touched.email && formik.errors.email ? null : "white"
                }
                sx={getAuthGlassFieldSx(theme)}
              />
            </Grid>

            <Grid item xs={12}>
              <AuthPasswordField
                variant="outlined"
                label={
                  formik.touched.password && formik.errors.password
                    ? formik.errors.password
                    : "Password"
                }
                required
                fullWidth
                id="password"
                name="password"
                showPsw={showPsw}
                onToggle={() => setShowPsw((v) => !v)}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                className={
                  formik.touched.password && formik.errors.password ? null : "white"
                }
                inputProps={{
                  autoComplete: "new-password",
                  enterKeyHint: "go",
                  autoCorrect: "off",
                  autoCapitalize: "none",
                  spellCheck: false,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <LoadingButton
                loading={load}
                variant="contained"
                fullWidth
                type="submit"
                disableElevation
                endIcon={load ? null : <LeftArrowIcon />}
                sx={{
                  ...getAuthPrimaryButtonSx(theme),
                  mt: 0.5,
                }}
              >
                {autoApproveUser ? "Create free account" : "Request account"}
              </LoadingButton>
            </Grid>

          </Grid>
        </Box>
      </AuthPageLayout>
    </>
  );
}
